import axios from 'axios';

// The Netlify function endpoint
const API = '/.netlify/functions/ai';

/**
 * AI Service for ebook generation
 * All prompts designed for KDP-compliant, high-quality output
 */
export const aiService = {
  // Helper for Netlify function calls
  _callFunction: async (action, payload) => {
    try {
      const response = await axios.post(API, { action, payload });
      return response.data;
    } catch (error) {
      console.error(`Error in ${action}:`, error);
      throw error;
    }
  },

  /**
   * Generate 7 magnetic book titles optimized for KDP
   */
  generateTitles: async (topic) => {
    const data = await aiService._callFunction('generateTitles', { topic });
    return data.titles;
  },

  /**
   * Generate 12-chapter Table of Contents with logical transformation
   */
  generateOutline: async (title, topic) => {
    const data = await aiService._callFunction('generateOutline', { title, topic });
    return data.chapters;
  },

  /**
   * Generate chapter content (2,500-3,500 words) — KDP publication quality
   */
  generateChapterContent: async (chapter, bookTitle, bookTopic, chapterNumber, totalChapters) => {
    const data = await aiService._callFunction('generateChapterContent', {
      chapter,
      bookTitle,
      bookTopic,
      chapterNumber,
      totalChapters
    });
    return data.content;
  },

  /**
   * Expand content — make it more detailed and comprehensive
   */
  expandContent: async (content, chapterTitle) => {
    const data = await aiService._callFunction('expandContent', {
      content,
      chapterTitle,
      operation: 'expand'
    });
    return data.content;
  },

  /**
   * Rewrite content — improve clarity, flow, and human voice
   */
  rewriteContent: async (content, chapterTitle) => {
    const data = await aiService._callFunction('expandContent', {
      content,
      chapterTitle,
      operation: 'rewrite'
    });
    return data.content;
  },

  /**
   * Improve content — enhance quality and add depth
   */
  improveContent: async (content, chapterTitle) => {
    const data = await aiService._callFunction('expandContent', {
      content,
      chapterTitle,
      operation: 'improve'
    });
    return data.content;
  },

  /**
   * Humanize content — make AI text indistinguishable from human writing
   */
  humanizeContent: async (content, chapterTitle) => {
    const data = await aiService._callFunction('expandContent', {
      content,
      chapterTitle,
      operation: 'humanize'
    });
    return data.content;
  },

  /**
   * Generate front matter (introduction, dedication, preface)
   */
  generateFrontMatter: async (bookTitle, authorName, topic, type) => {
    const data = await aiService._callFunction('generateFrontMatter', {
      bookTitle,
      authorName,
      topic,
      type
    });
    return data.content;
  },

  /**
   * Generate back matter (about author, resources)
   */
  generateBackMatter: async (bookTitle, authorName, topic, type) => {
    const data = await aiService._callFunction('generateBackMatter', {
      bookTitle,
      authorName,
      topic,
      type
    });
    return data.content;
  },

  /**
   * Generate KDP-optimized book description with HTML formatting
   */
  generateBookDescription: async (bookTitle, topic, chapters) => {
    const data = await aiService._callFunction('generateBookDescription', {
      bookTitle,
      topic,
      chapters
    });
    return data.description;
  },

  /**
   * Generate 7 KDP keywords, 2 categories, and target audience
   */
  generateKeywords: async (bookTitle, topic) => {
    const data = await aiService._callFunction('generateKeywords', {
      bookTitle,
      topic
    });
    return data;
  },

  /**
   * Run KDP compliance check on the book
   */
  kdpComplianceCheck: async (chapters, bookTitle) => {
    const data = await aiService._callFunction('kdpComplianceCheck', {
      chapters,
      bookTitle
    });
    return data;
  },

  /**
   * Generate cover image with "Modern Business" style
   */
  generateCover: async (title, subtitle, authorName, stylePrompt = '') => {
    const data = await aiService._callFunction('generateCover', {
      title,
      subtitle,
      authorName,
      stylePrompt
    });
    return {
      imageBase64: data.image_base64,
      mimeType: data.mime_type
    };
  },

  /**
   * Generate cover prompt for manual use with DALL-E, Midjourney, etc.
   */
  generateCoverPrompt: async (title, subtitle, authorName) => {
    const data = await aiService._callFunction('generateCoverPrompt', {
      title,
      subtitle,
      authorName
    });
    return data.prompt;
  },
};
